import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X, Package } from 'lucide-react';
import { useQuery } from 'react-query';
import { getPartTypes, getPartCodes } from '@/services/bomService';
import type { PartTypeOption, PartCodeRecord } from '@/services/bomService';

interface PartPickerBlockProps {
  data: unknown;
  onAction: (message: string) => void;
}

const PartPickerBlock: React.FC<PartPickerBlockProps> = ({ onAction }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTypeCode, setSelectedTypeCode] = useState('');
  const { t } = useTranslation('purchaseflow');

  // Fetch part types
  const { data: partTypes, isLoading: typesLoading } = useQuery<PartTypeOption[]>(
    'partTypes-inline',
    getPartTypes,
    { staleTime: 300000, enabled: isOpen }
  );

  // Fetch parts for selected type
  const { data: parts, isLoading: partsLoading } = useQuery<PartCodeRecord[]>(
    ['partCodes-inline', 'electronic_parts', selectedTypeCode],
    () => getPartCodes('electronic_parts', selectedTypeCode || undefined),
    { staleTime: 60000, enabled: isOpen }
  );

  // Filter by search
  const filtered = useMemo(() => {
    if (!parts) return [];
    if (!searchQuery) return parts.slice(0, 50); // cap for inline display
    const q = searchQuery.toLowerCase();
    return parts.filter(p =>
      p.part_number?.toLowerCase().includes(q) ||
      p.vendor_product?.toLowerCase().includes(q) ||
      p.vendor_spec?.toLowerCase().includes(q) ||
      p.brand?.toLowerCase().includes(q)
    ).slice(0, 50);
  }, [parts, searchQuery]);

  const handleSelect = (part: PartCodeRecord) => {
    const desc = part.vendor_product || part.part_number;
    const spec = part.vendor_spec || '';
    onAction(`Add ${part.part_number} ${desc}${spec ? ', spec: ' + spec : ''}`);
  };

  if (!isOpen) {
    return (
      <div className="mt-2">
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30 border border-blue-200 dark:border-blue-800 transition-colors"
        >
          <Search className="w-3.5 h-3.5" />
          {t('quickActions.addVendorPart')}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
      {/* Header */}
      <div className="bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
          <Package className="w-3.5 h-3.5" />
          選擇零件 — Part Catalog
        </div>
        <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Filters */}
      <div className="px-3 pt-2 pb-1 flex gap-2">
        <select
          value={selectedTypeCode}
          onChange={e => setSelectedTypeCode(e.target.value)}
          disabled={typesLoading}
          className="text-[11px] px-2 py-1 rounded border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 flex-shrink-0"
        >
          <option value="">全部類別</option>
          {partTypes?.map(type => (
            <option key={type.part_code} value={type.part_code}>
              {type.part_code} - {type.part_type_zh}
            </option>
          ))}
        </select>
        <div className="relative flex-1">
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="搜尋品號、品名、規格..."
            className="w-full text-[11px] pl-7 pr-2 py-1 rounded border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:ring-1 focus:ring-emerald-500 focus:border-transparent"
          />
          <Search className="absolute left-2 top-1.5 w-3.5 h-3.5 text-gray-400" />
        </div>
      </div>

      {/* Table */}
      <div className="max-h-48 overflow-y-auto">
        {partsLoading ? (
          <div className="flex items-center justify-center py-6 text-xs text-gray-400">
            <div className="animate-spin w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full mr-2" />
            Loading...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-6 text-xs text-gray-400">
            {searchQuery ? '無符合條件的零件' : '請選擇類別或搜尋'}
          </div>
        ) : (
          <table className="w-full text-[11px]">
            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
              <tr className="text-gray-500 dark:text-gray-400">
                <th className="text-left px-3 py-1.5 font-medium">品號</th>
                <th className="text-left px-2 py-1.5 font-medium">廠牌</th>
                <th className="text-left px-2 py-1.5 font-medium">品名</th>
                <th className="text-left px-2 py-1.5 font-medium hidden sm:table-cell">規格</th>
                <th className="px-2 py-1.5 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filtered.map((part, i) => (
                <tr
                  key={part.part_number || i}
                  className="hover:bg-emerald-50 dark:hover:bg-emerald-900/10 cursor-pointer transition-colors"
                  onClick={() => handleSelect(part)}
                >
                  <td className="px-3 py-1.5 font-mono text-gray-800 dark:text-gray-200">{part.part_number}</td>
                  <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400">{part.brand}</td>
                  <td className="px-2 py-1.5 text-gray-800 dark:text-gray-200 truncate max-w-[150px]">{part.vendor_product}</td>
                  <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400 truncate max-w-[120px] hidden sm:table-cell">{part.vendor_spec}</td>
                  <td className="px-2 py-1.5">
                    <button
                      onClick={e => { e.stopPropagation(); handleSelect(part); }}
                      className="px-2 py-0.5 text-[10px] font-medium text-white bg-emerald-500 rounded hover:bg-emerald-600 transition-colors"
                    >
                      選擇
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer count */}
      <div className="px-3 py-1.5 border-t border-gray-100 dark:border-gray-700 text-[10px] text-gray-400">
        {filtered.length} 筆{searchQuery && ` (搜尋: "${searchQuery}")`}
      </div>
    </div>
  );
};

export default PartPickerBlock;
